import type { Student } from "@/types";

/**
 * 아파트 라인 그룹핑 — 현관/엘리베이터를 공유하는 호수 묶음.
 *
 * 한국 아파트는 일반적으로 아래 패턴 중 하나:
 *   (a) 한 동 전체 1개 엘리베이터 공용 (복도식)
 *   (b) 2세대/층 × 1엘 (1-2호 라인 / 3-4호 라인 별도 코어 — 판상형)
 *   (c) 4세대/층 × 1엘 (1~4호 한 엘리베이터 — 타워형)
 *   (d) 2세대/층 × 2엘 (복식)
 *
 * 정확한 판별은 카카오 주소검색 / 국토부 건축물대장이 필요하지만, 우리는 휴리스틱 + 관리자 오버라이드로 처리.
 *
 * 휴리스틱:
 *   - 호수 앞 1~2자리 정수 = 라인 번호 (예: 1501호 → 15층 01라인)
 *   - 1·2호 라인 / 3·4호 라인처럼 인접한 두 호수는 같은 엘리베이터로 가정 (판상형 기본값)
 *   - 관리자가 아파트별로 라인 패턴 오버라이드 가능
 */

export type LinePattern =
  | "1-2|3-4"      // 판상형 기본 — 1·2호 한 엘리, 3·4호 다른 엘리
  | "1-2-3-4"      // 타워형 — 전부 같은 엘리
  | "1|2|3|4"      // 각 호수마다 별도 엘리 (드묾)
  | "corridor"     // 복도식 — 전부 한 엘리
  | "custom";      // 사용자 지정

export interface LineInfo {
  pattern: LinePattern;
  groupIndex: number;    // 해당 호수의 그룹 번호 (0=첫번째 라인, 1=두번째 라인, ...)
  groupLabel: string;    // "1·2호 라인" 등
  elevatorHint: string;  // 기사 안내 문구
  unitNumber: string;    // 호수 끝자리 (1호, 2호 등)
}

function parseHoParts(ho: string): { floor: number; unit: number } | null {
  // 306호 → 3층 06호 / 1501호 → 15층 01호 / 1호 → 0층 1호
  const s = ho.replace(/\D/g, "");
  if (!s) return null;
  if (s.length <= 2) return { floor: 0, unit: Number(s) };
  const unit = Number(s.slice(-2));
  const floor = Number(s.slice(0, -2));
  return { floor, unit };
}

export function classifyLine(
  student: Pick<Student, "buildingHo" | "buildingName" | "lineOverride">,
  pattern: LinePattern = "1-2|3-4",
): LineInfo {
  const parts = parseHoParts(student.buildingHo || "");
  const unit = parts?.unit ?? 0;
  const unitNumber = unit ? `${unit}호` : "";

  if (student.lineOverride) {
    return {
      pattern: "custom",
      groupIndex: 0,
      groupLabel: student.lineOverride,
      elevatorHint: student.lineOverride,
      unitNumber,
    };
  }

  let groupIndex = 0;
  let groupLabel = "";
  let elevatorHint = "";

  switch (pattern) {
    case "1-2|3-4":
      if (unit === 0) {
        groupLabel = "호수 불명";
      } else if (unit <= 2) {
        groupIndex = 0;
        groupLabel = "1·2호 라인";
        elevatorHint = "왼쪽(1·2호) 엘리베이터";
      } else if (unit <= 4) {
        groupIndex = 1;
        groupLabel = "3·4호 라인";
        elevatorHint = "오른쪽(3·4호) 엘리베이터";
      } else if (unit <= 6) {
        groupIndex = 2;
        groupLabel = "5·6호 라인";
        elevatorHint = "5·6호 엘리베이터";
      } else {
        groupIndex = Math.floor((unit - 1) / 2);
        groupLabel = `${unit - (unit % 2 ? 0 : 1)}·${unit + (unit % 2 ? 1 : 0)}호 라인`;
        elevatorHint = groupLabel;
      }
      break;
    case "1-2-3-4":
      groupIndex = 0;
      groupLabel = "공용 엘리베이터";
      elevatorHint = "1~4호 공용 엘리베이터";
      break;
    case "corridor":
      groupIndex = 0;
      groupLabel = "복도식";
      elevatorHint = "복도식(중앙 엘리베이터)";
      break;
    case "1|2|3|4":
      groupIndex = unit - 1;
      groupLabel = `${unit}호 전용`;
      elevatorHint = `${unit}호 전용 엘리베이터`;
      break;
    default:
      groupLabel = "알 수 없음";
  }

  return { pattern, groupIndex, groupLabel, elevatorHint, unitNumber };
}

/**
 * 같은 아파트 같은 라인으로 묶인 방문지 집합을 반환 — 배송 동선 시각화용.
 */
export function groupByLine(
  students: Student[],
  pattern: LinePattern = "1-2|3-4",
): { [buildingDong: string]: { line: LineInfo; students: Student[] }[] } {
  const byBuildingDong: Record<string, Map<number, { line: LineInfo; students: Student[] }>> = {};
  for (const s of students) {
    const key = s.buildingDong || "-";
    if (!byBuildingDong[key]) byBuildingDong[key] = new Map();
    const line = classifyLine(s, pattern);
    const existing = byBuildingDong[key].get(line.groupIndex);
    if (existing) existing.students.push(s);
    else byBuildingDong[key].set(line.groupIndex, { line, students: [s] });
  }
  const out: Record<string, { line: LineInfo; students: Student[] }[]> = {};
  for (const [k, v] of Object.entries(byBuildingDong)) {
    out[k] = [...v.values()].sort((a, b) => a.line.groupIndex - b.line.groupIndex);
    // 각 라인 내 호수는 층 내림차순 (옥상→1층 순회)
    for (const lg of out[k]) {
      lg.students.sort((a, b) => {
        const ap = parseHoParts(a.buildingHo)?.floor ?? 0;
        const bp = parseHoParts(b.buildingHo)?.floor ?? 0;
        return bp - ap;
      });
    }
  }
  return out;
}
